import React, { FC, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { Select, Spin, Tag } from 'antd';
import debounce from 'lodash/debounce';
import { Query } from 'src/core/stores/data-store';
import { DefaultRoleSummaryStore, DefaultRoleSummary } from 'src/stores/default-roles-store';
import { Selector } from 'src/common/types';
import { generateSelectorOdataFilter, getRoleDisplayName } from 'src/common/utils';
import { ImmutableObject } from '@hookstate/core';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';

const tagRender = (props: CustomTagProps) => {
  const { label, closable, onClose } = props;
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag onMouseDown={onPreventMouseDown} closable={closable} onClose={onClose}>
      {getRoleDisplayName(`${label}`)}
    </Tag>
  );
};

const DefaultRolesSelect: FC<
  {
    onChange?: (values: { value: string; label: string; selector: Selector }[], index: number | undefined) => void;
    exclude?: string[];
    value?: { value: string; label: string }[];
    minSelector?: Selector;
    maxSelector?: Selector;
    placeholder?: string;
    partnersOnly?: boolean;
    excludeNames?: string[];
    index: number | undefined;
  } & WithTranslation
> = ({
  t,
  onChange,
  exclude,
  index,
  excludeNames,
  value,
  minSelector = Selector.Drillhole,
  maxSelector = Selector.Admin,
  placeholder,
  partnersOnly,
}) => {
  const roleStore = container.get(DefaultRoleSummaryStore);
  const rolesState = roleStore.state;
  const [currentValues, setValues] = useState((value || []).map((x) => ({ value: x.value, label: x.label || `Role ${x.value}` })));

  const load = (search: string | undefined = undefined) => {
    let filter = [generateSelectorOdataFilter(minSelector, maxSelector)];

    if ((exclude || []).length !== 0) filter = [...filter, `id ne ${exclude?.join(' and id ne ')}`];

    if (partnersOnly != null) filter = [...filter, `PartnersOnly eq ${partnersOnly}`];

    roleStore.load({
      searchQuery: search,
      orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
      skip: 0,
      take: 1000000,
      filter,
    } as Query);
  };

  useEffect(() => {
    load();
  }, [currentValues, partnersOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = debounce((value: string) => {
    load(value);
  }, 800);

  const onSelect = (nextValues: { value: string; label: string }[], options: any) => {
    setValues(nextValues);
    onChange &&
      onChange(
        (options as any[]).map((x) => ({ value: x.value, label: x.label, selector: x.selector })),
        index
      );
  };

  return (
    <>
      <Select
        className="role-select"
        mode="multiple"
        loading={!!rolesState.isBusy.get()}
        labelInValue
        placeholder={placeholder || t('Select default roles')}
        onChange={onSelect}
        style={{ width: '100%' }}
        allowClear
        showSearch
        value={currentValues}
        notFoundContent={rolesState.isBusy.get() ? <Spin size="small" /> : undefined}
        filterOption={false}
        showArrow={false}
        onSearch={onSearch}
        optionLabelProp="label"
        tagRender={tagRender}
        onDropdownVisibleChange={(visible) => {
          if (!visible) load();
        }}
      >
        {(rolesState.items.value || [])
          .filter((x) => !(excludeNames || []).any((y) => y === x.name))
          .map((o: ImmutableObject<DefaultRoleSummary>) => (
            <Select.Option key={o.id} value={o.id} selector={o.selector} label={o.name}>
              {getRoleDisplayName(o.name)}
            </Select.Option>
          ))}
      </Select>
    </>
  );
};

export default withTranslation()(DefaultRolesSelect);
